import {
  Children,
  cloneElement,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useState,
} from 'react';

import {
  PlotTooltip,
  type PlotTooltipPlacement,
  type TooltipPosition,
} from './plot-tooltip';

export const getCursorPosition = (event: {
  clientX: number;
  clientY: number;
}): TooltipPosition => ({ x: event.clientX, y: event.clientY });

export const useCursorTooltipPosition = () => {
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const trackCursor = useCallback(
    (event: { clientX: number; clientY: number }) =>
      setPosition(getCursorPosition(event)),
    []
  );
  const clearCursor = useCallback(() => setPosition(null), []);
  return { position, trackCursor, clearCursor };
};

type HoverableMarkProps = {
  onMouseMove?: (event: MouseEvent<SVGElement>) => void;
  onMouseLeave?: (event: MouseEvent<SVGElement>) => void;
};

type CursorTooltipProps = {
  label: ReactNode;
  children: ReactElement<HoverableMarkProps>;
  placement?: PlotTooltipPlacement;
  isDisabled?: boolean;
};

export const CursorTooltip = ({
  label,
  children,
  placement = 'vertical',
  isDisabled = false,
}: CursorTooltipProps) => {
  const { position, trackCursor, clearCursor } = useCursorTooltipPosition();
  const mark = Children.only(children);

  const handleMouseMove = (event: MouseEvent<SVGElement>) => {
    mark.props.onMouseMove?.(event);
    if (!isDisabled) trackCursor(event);
  };
  const handleMouseLeave = (event: MouseEvent<SVGElement>) => {
    mark.props.onMouseLeave?.(event);
    clearCursor();
  };

  return (
    <>
      {cloneElement(mark, {
        onMouseMove: handleMouseMove,
        onMouseLeave: handleMouseLeave,
      })}
      {position !== null && !isDisabled && (
        <PlotTooltip
          isVisible
          space="viewport"
          placement={placement}
          x={position.x}
          y={position.y}
        >
          {label}
        </PlotTooltip>
      )}
    </>
  );
};
